// React
import PropTypes from "prop-types";
import { useContext, useEffect, useRef, useState } from "react";

// @mui material components
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import {
  Card,
  CircularProgress,
  Fade,
  Grid,
  Icon,
  InputLabel,
  Modal,
  Tooltip,
} from "@mui/material";
import SoftBox from "components/CheckBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";

export default function ModalCargarComprobante({ open, handleClose, refetch, pedido, token }) {
  const [file, setFile] = useState(null);
  const inputRef = useRef(null);
  const { handleSnackbar } = useContext(MessageManager);
  const [getMedioPago, { data: medioPago }] = useLazyQuery(gql`
    query getMedioPago($id: ID!, $token: String) {
      medioPago(id: $id, token: $token) {
        id
        nombre
        alias
        cbu
        titular
      }
    }
  `);
  const [cargarComprobantePago, { loading }] = useMutation(gql`
    mutation cargarComprobantePago($id: ID!, $comprobantes: [Upload!]!, $token: String) {
      cargarComprobantePago(id: $id, comprobantes: $comprobantes, token: $token) {
        id
      }
    }
  `);
  const [error, setError] = useState(null);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    handleSnackbar("Alias copiado al portapapeles", "success");
  };

  const handleSave = async () => {
    let id = pedido.venta.id;

    await cargarComprobantePago({ variables: { id, comprobantes: [file], token } })
      .then(() => {
        refetch();
        handleSnackbar("Comprobante cargado con éxito", "success");
      })
      .catch((error) => {
        handleSnackbar(error.message, "error");
        refetch();
      });
    handleClose();
  };

  useEffect(() => {
    getMedioPago({ variables: { id: 1, token } });
    setFile(null);
  }, [open]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={() => {
        if (!loading) {
          handleClose();
        }
      }}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            maxHeight: { xs: "90%", sm: "80%" },
            width: { xs: "80%", sm: "40%" },
            overflowY: "auto",
            p: 3,
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} display="flex" flexDirection="row" justifyContent="space-between">
              <SoftTypography variant="h6" align="center">
                Cargar el comprobante
              </SoftTypography>
              <Tooltip title="Cerrar" placement="top">
                <Icon
                  sx={{ color: "primary", cursor: "pointer", mx: 0.5 }}
                  fontSize="small"
                  onClick={handleClose}
                >
                  close
                </Icon>
              </Tooltip>
            </Grid>
            <Grid item xs={12}>
              <SoftBox display="flex" flexDirection="column" gap={2}>
                <SoftBox>
                  <InputLabel htmlFor="modulo">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Alias
                    </SoftTypography>
                  </InputLabel>
                  <SoftTypography variant="h6" fontWeight="bold" color="primary">
                    {medioPago?.medioPago?.alias}
                  </SoftTypography>
                </SoftBox>
                {medioPago?.medioPago?.cbu && (
                  <SoftBox>
                    <InputLabel htmlFor="modulo">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        CBU
                      </SoftTypography>
                    </InputLabel>
                    <SoftTypography variant="h6" fontWeight="bold" color="primary">
                      {medioPago?.medioPago?.cbu}
                    </SoftTypography>
                  </SoftBox>
                )}
                <SoftBox>
                  <InputLabel htmlFor="modulo">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Titular
                    </SoftTypography>
                  </InputLabel>
                  <SoftTypography variant="h6" fontWeight="bold" color="primary">
                    {medioPago?.medioPago?.titular}
                  </SoftTypography>
                </SoftBox>
                <SoftButton
                  circular
                  color="uproGreen"
                  onClick={() => handleCopy(medioPago?.medioPago?.alias)}
                >
                  Copiar alias&nbsp;
                  <Icon>content_copy</Icon>
                </SoftButton>
                <SoftButton
                  circular
                  color={file ? "uproGreen" : "uproYellow"}
                  onClick={() => inputRef.current.click()}
                >
                  {file
                    ? file.name.length > 20
                      ? file.name.substring(0, 20) + "..."
                      : file.name
                    : "Subir comprobante de pago"}
                  &nbsp;
                  <Icon>{file ? "done" : "file_upload"}</Icon>
                </SoftButton>
                <input
                  ref={inputRef}
                  type="file"
                  style={{ display: "none" }}
                  accept=".pdf, .docx, .jpg, .jpeg, .png"
                  onChange={(e) => {
                    if (e.target.files[0] && e.target.files[0].size < 5000000) {
                      setFile(e.target.files[0]);
                    } else {
                      handleSnackbar("El archivo debe pesar menos de 5MB", "error");
                    }
                  }}
                />
              </SoftBox>
            </Grid>
            <Grid
              item
              xs={12}
              display="flex"
              justifyContent="flex-end"
              sx={{
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Grid>
                <SoftButton
                  color="uproGreen"
                  circular
                  fullWidth={{
                    xs: true,
                    sm: false,
                  }}
                  onClick={handleSave}
                  disabled={loading || !file}
                >
                  Guardar&nbsp;
                  {loading ? <CircularProgress size={15} color="inherit" /> : <Icon>save</Icon>}
                </SoftButton>
              </Grid>
            </Grid>
          </Grid>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalCargarComprobante.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  refetch: PropTypes.func,
  pedido: PropTypes.object,
  token: PropTypes.string,
};
